import { useMutation } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';

export type TagMatchSource = 'contact' | 'enrollment';

export interface TagBulkChargeFamilyPreview {
  parentId: string;
  parentName: string;
  parentEmail: string;
  studentId: string;
  studentNames: string[];
  hasActiveStudent: boolean;
  hasPaymentMethodOnFile: boolean;
  matchedTags: Array<{ id: string; name: string; source: TagMatchSource }>;
}

export interface ResolveTagBulkChargeAudienceParams {
  contactTagIds?: Array<string>;
  enrollmentTagIds?: Array<string>;
}

export interface ResolveTagBulkChargeAudienceResponse {
  families: Array<TagBulkChargeFamilyPreview>;
  total: number;
}

/** `installments` = the family amount split across the dates (payment plan). */
export type TagBulkChargeMode = 'now' | 'pay_later' | 'installments';

export interface BulkChargeByTagsFamilyInput {
  parentId: string;
  studentId: string;
  /** Family total for the run; installments split it across the scheduled dates. */
  amount: number;
}

export interface BulkChargeByTagsParams {
  families: Array<BulkChargeByTagsFamilyInput>;
  chargeName: string;
  transactionCodeId?: string;
  mode: TagBulkChargeMode;
  /** One scheduled charge per date; installments divide the family amount across them. */
  scheduledDates?: Array<string>;
}

export interface BulkChargeByTagsResponse {
  success: boolean;
  message: string;
  bulkPaymentId?: string;
  /** One id per family charge call — history may show multiple rows. */
  bulkPaymentIds?: string[];
  summary?: {
    total: number;
    charged: number;
    scheduled: number;
    skipped: number;
    failed: number;
    families: number;
  };
  results?: Array<{
    parentId: string;
    studentId: string;
    amount: number;
    scheduledDate?: string | null;
    status: string;
    reason?: string;
    invoiceId?: string;
  }>;
}

export type TagBulkChargeOutcome =
  | { type: 'success'; message: string }
  | { type: 'partial'; message: string }
  | { type: 'error'; message: string };

/**
 * Maps tag bulk charge summary to toast severity.
 * Skips (already charged / re-run guard) are never treated as a hard failure.
 */
export const getTagBulkChargeOutcome = (
  data: BulkChargeByTagsResponse,
): TagBulkChargeOutcome => {
  const charged = data.summary?.charged ?? 0;
  const scheduled = data.summary?.scheduled ?? 0;
  const skipped = data.summary?.skipped ?? 0;
  const failed = data.summary?.failed ?? 0;
  const applied = charged + scheduled;
  const counts = `Charged/scheduled: ${applied}, skipped: ${skipped}, failed: ${failed}`;
  const base = data.message || 'Tag bulk charge completed';

  if (!data.success && failed === 0 && skipped === 0 && applied === 0) {
    return {
      type: 'error',
      message: data.message || 'Tag bulk charge failed',
    };
  }

  if (failed === 0 && skipped === 0) {
    return { type: 'success', message: base };
  }

  if (failed === 0 && skipped > 0) {
    return {
      type: applied === 0 ? 'success' : 'partial',
      message: `${base}. ${counts}`,
    };
  }

  if (failed > 0 && (applied > 0 || skipped > 0)) {
    return {
      type: 'partial',
      message: `${base}. ${counts}`,
    };
  }

  return {
    type: 'error',
    message: base,
  };
};

/** V1 — payment-processor/bulk-charge-by-tags/resolve */
export const resolveTagBulkChargeAudienceV1API = async (
  params: ResolveTagBulkChargeAudienceParams,
) => {
  const response = await baseAPI.post<ResolveTagBulkChargeAudienceResponse>(
    '/payment-processor/bulk-charge-by-tags/resolve',
    params,
  );
  return response.data;
};

/** V2 — PaymentService.settle + SubscriptionInvoice (no v1 delegation). */
export const resolveTagBulkChargeAudienceV2API = async (
  params: ResolveTagBulkChargeAudienceParams,
) => {
  const response = await baseAPI.post<ResolveTagBulkChargeAudienceResponse>(
    '/v2/tag-bulk-charges/resolve',
    params,
  );
  return response.data;
};

export const resolveTagBulkChargeAudienceAPI = async (
  params: ResolveTagBulkChargeAudienceParams,
  isV2 = false,
) =>
  isV2
    ? resolveTagBulkChargeAudienceV2API(params)
    : resolveTagBulkChargeAudienceV1API(params);

/** V1 — payment-processor/bulk-charge-by-tags */
export const bulkChargeByTagsV1API = async (params: BulkChargeByTagsParams) => {
  const response = await baseAPI.post<BulkChargeByTagsResponse>(
    '/payment-processor/bulk-charge-by-tags',
    params,
  );
  return response.data;
};

/** V2 — /v2/tag-bulk-charges */
export const bulkChargeByTagsV2API = async (params: BulkChargeByTagsParams) => {
  const response = await baseAPI.post<BulkChargeByTagsResponse>(
    '/v2/tag-bulk-charges',
    params,
  );
  return response.data;
};

export const bulkChargeByTagsAPI = async (
  params: BulkChargeByTagsParams,
  isV2 = false,
) => (isV2 ? bulkChargeByTagsV2API(params) : bulkChargeByTagsV1API(params));

export type ResolveTagBulkChargeAudienceMutationInput = {
  params: ResolveTagBulkChargeAudienceParams;
  isV2?: boolean;
};

export type BulkChargeByTagsMutationInput = {
  params: BulkChargeByTagsParams;
  isV2?: boolean;
};

export const useResolveTagBulkChargeAudience = () =>
  useMutation({
    mutationFn: ({ params, isV2 = false }: ResolveTagBulkChargeAudienceMutationInput) =>
      resolveTagBulkChargeAudienceAPI(params, isV2),
  });

export const useBulkChargeByTags = () =>
  useMutation({
    mutationFn: ({ params, isV2 = false }: BulkChargeByTagsMutationInput) =>
      bulkChargeByTagsAPI(params, isV2),
  });
